import { buildTitle } from "@/lib/seo-quality";
import { createFileRoute, Link } from "@tanstack/react-router";
import { PageHeader } from "./fordon";
import { faqSchema } from "@/components/HubEditorial";
import { AdSlot } from "@/components/AdSlot";
import { RelatedLinks } from "@/components/RelatedLinks";
import { Sparkline, Delta } from "@/components/StockCard";
import { getPriceIndex } from "@/lib/price-index.functions";
import { getStockBoard } from "@/lib/stocks.functions";
import { STOCK_ISSUERS } from "@/lib/stocks";

const URL = "https://www.akhusbilfritid.se/marknad";
const TITLE = buildTitle("Marknadsdata husbil och husvagn – priser och statistik");
const DESC =
  "Marknadsrum för fritidsfordon: prisindex månad för månad, medianpriser per segment och märke, aktiekurser bakom märkena och gratis värdering.";

const FAQ = [
  {
    q: "Vad är skillnaden mellan prisindex och prisstatistik?",
    a: "Prisstatistiken svarar på vad en husbil kostar just nu per segment, märke och årsmodell och räknas om vid varje sidvisning. Prisindexet svarar på om priserna rör sig uppåt eller nedåt och låser en mätpunkt per månad som aldrig räknas om i efterhand. Statistiken är termometern, indexet är kurvan.",
  },
  {
    q: "Var kommer siffrorna ifrån?",
    a: "Annonspriserna kommer från de annonser vi själva följer dygnet runt, slutpriserna från affärer vi kan verifiera, tillbehörspriserna från vår egen butik och aktiekurserna från publika börsdatakällor med några minuters fördröjning. Ingen del av materialet är inköpt branschdata.",
  },
  {
    q: "Vilken sida ska jag börja på?",
    a: "Ska du sälja: börja med värderingen och läs sedan indexet för att avgöra tidpunkten. Ska du köpa: börja med prisstatistiken för att se nivån i ditt segment och kontrollera med indexet om nivån är på väg ner. Vill du förstå branschen i stort: börja med börsen, där tillverkarnas läge syns först.",
  },
  {
    q: "Får jag använda era siffror?",
    a: "Ja. Prisindexet finns som öppen CSV och JSON och som färdig inbäddningswidget, fritt att citera så länge du anger akhusbilfritid.se som källa.",
  },
  {
    q: "Hur ofta uppdateras marknadsrummet?",
    a: "Prisstatistiken räknas om vid varje sidvisning. Indexets pågående månad skrivs om varje natt och låses vid månadsskiftet. Aktiekurserna hämtas löpande och cachas i tio minuter.",
  },
];

export const Route = createFileRoute("/marknad")({
  codeSplitGroupings: [["loader", "component"]],
  loader: async () => {
    const [index, board] = await Promise.all([
      getPriceIndex(),
      getStockBoard().catch(() => ({ quotes: [], updatedAt: new Date().toISOString() })),
    ]);
    return { index, board };
  },
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      {
        name: "keywords",
        content:
          "marknadsdata husbil, prisindex husbil, prisstatistik husvagn, husbilspriser 2026, kabe aktie, värdera husbil",
      },
      { property: "og:type", content: "website" },
      { property: "og:title", content: "Marknadsdata för husbil & husvagn — allt på ett ställe" },
      { property: "og:description", content: DESC },
      { property: "og:url", content: URL },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(faqSchema(FAQ)) },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          name: TITLE,
          description: DESC,
          url: URL,
          inLanguage: "sv-SE",
          isPartOf: { "@type": "WebSite", name: "AK Husbil & Fritid", url: "https://www.akhusbilfritid.se" },
        }),
      },
    ],
  }),
  component: Page,
});

const kr = (v: number) => `${Math.round(v).toLocaleString("sv-SE")} kr`;

type Card = {
  to: string;
  eyebrow: string;
  title: string;
  body: string;
  cta: string;
};

const CARDS: Card[] = [
  {
    to: "/prisindex",
    eyebrow: "Rörelsen",
    title: "AK Prisindex",
    body: "Medianpris, prisspann och månadsförändring för husbil och husvagn. Varje månad låses som en egen mätpunkt och räknas aldrig om i efterhand. Finns som öppen CSV, JSON och inbäddningswidget.",
    cta: "Öppna prisindexet",
  },
  {
    to: "/prisstatistik-husbil",
    eyebrow: "Nivån",
    title: "Prisstatistik",
    body: "Medianpris och vanligt spann per segment, fabrikat och årsmodell, räknat om vid varje sidvisning på lagret vi följer. Här ser du vad ditt segment faktiskt ligger på i kronor.",
    cta: "Se prisstatistiken",
  },
  {
    to: "/borsen",
    eyebrow: "Branschen",
    title: "Fritidsbörsen",
    body: "Kurs och utveckling för Kabe Group, Trigano, Knaus Tabbert, Thor, Dometic och Thule — bolagen som äger märkena. Deras orderböcker vänder ett halvår före begagnatpriserna.",
    cta: "Se bolagen bakom märkena",
  },
  {
    to: "/vardera-husbil",
    eyebrow: "Ditt fordon",
    title: "Värdering",
    body: "Från marknadsnivå till en siffra för just ditt fordon: mil, fukt, däckens ålder och servicehistorik räknas in, och varje multiplikator syns i klartext.",
    cta: "Värdera gratis",
  },
  {
    to: "/salda",
    eyebrow: "Facit",
    title: "Sålda fordon",
    body: "Arkivet över fordon som faktiskt bytt ägare, med det pris affären gjordes på. Den enda kolumnen som inte är en förhoppning utan ett utfall.",
    cta: "Öppna arkivet",
  },
  {
    to: "/salja-husbil",
    eyebrow: "Beslutet",
    title: "Sälja husbil",
    body: "Vad privat annons, förmedling och inbyte ger i kronor efter avdrag, och vad det kostar per månad att vänta när indexet pekar nedåt.",
    cta: "Läs säljguiden",
  },
];

function Page() {
  const { index, board } = Route.useLoaderData();
  const husbil = index.latest.husbil;
  const husvagn = index.latest.husvagn;
  const kabe = board.quotes.find((q) => q.id === "kabe");
  const kabeIssuer = STOCK_ISSUERS.find((i) => i.id === "kabe");

  return (
    <>
      <PageHeader
        label="Marknadsrummet"
        title={
          <>
            All <span className="font-italic-display text-accent">marknadsdata</span> på ett ställe
          </>
        }
        sub="Prisindex, prisstatistik, sålda fordon, värdering och börsen för bolagen bakom märkena. Samma underlag, fyra olika frågor: vad kostar det, vart är det på väg, vad är mitt fordon värt och hur går branschen?"
      />

      <section className="container-edge pt-8 md:pt-12">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {husbil && (
            <div className="rounded-3xl border border-border bg-card p-6">
              <p className="text-xs uppercase tracking-wider text-muted-foreground">Median husbil</p>
              <p className="mt-2 font-display text-3xl leading-none text-accent">{kr(husbil.medianPrice)}</p>
              <p className="mt-2 text-sm text-muted-foreground">
                Index {husbil.index.toLocaleString("sv-SE")} · {husbil.n} objekt ·{" "}
                <Delta value={husbil.changePct} />
              </p>
            </div>
          )}
          {husvagn && (
            <div className="rounded-3xl border border-border bg-card p-6">
              <p className="text-xs uppercase tracking-wider text-muted-foreground">Median husvagn</p>
              <p className="mt-2 font-display text-3xl leading-none text-accent">{kr(husvagn.medianPrice)}</p>
              <p className="mt-2 text-sm text-muted-foreground">
                Index {husvagn.index.toLocaleString("sv-SE")} · {husvagn.n} objekt ·{" "}
                <Delta value={husvagn.changePct} />
              </p>
            </div>
          )}
          {kabe && kabe.price != null && kabeIssuer && (
            <div className="rounded-3xl border border-border bg-card p-6">
              <p className="text-xs uppercase tracking-wider text-muted-foreground">
                {kabeIssuer.company}
              </p>
              <p className="mt-2 font-display text-3xl leading-none text-accent">
                {kabe.price.toLocaleString("sv-SE", { maximumFractionDigits: 2 })} {kabe.currency}
              </p>
              <p className="mt-2 text-sm text-muted-foreground">
                <Delta value={kabe.yearChangePct} suffix=" på ett år" />
              </p>
              {kabe.series.length > 2 && <Sparkline series={kabe.series} className="mt-3" />}
            </div>
          )}
        </div>
      </section>

      <section className="container-edge grid gap-4 pt-10 md:grid-cols-2 md:pt-14">
        {CARDS.map((c) => (
          <Link
            key={c.to}
            to={c.to}
            className="group flex flex-col rounded-3xl border border-border bg-card p-6 transition hover:border-accent/50"
          >
            <p className="text-xs uppercase tracking-wider text-muted-foreground">{c.eyebrow}</p>
            <h2 className="mt-2 font-display text-2xl">{c.title}</h2>
            <p className="mt-3 flex-1 text-[0.975rem] leading-relaxed text-muted-foreground">{c.body}</p>
            <span className="mt-4 inline-flex items-center font-medium text-accent">
              {c.cta}
              <span aria-hidden className="ml-2 transition group-hover:translate-x-1">
                →
              </span>
            </span>
          </Link>
        ))}
      </section>

      <section className="container-edge pt-10">
        <AdSlot variant="in-article" />
      </section>

      <section className="container-edge pt-12 md:pt-16">
        <div className="prose-editorial max-w-3xl space-y-5 text-[0.975rem] leading-relaxed text-muted-foreground">
          <h2 className="font-display text-2xl text-foreground">Så hänger siffrorna ihop</h2>
          <p>
            Fyra olika mätningar av samma marknad är inte fyra sanningar utan en kedja. Börsen rör sig
            först, eftersom tillverkarnas orderböcker vänder innan något syns i en annons. Därefter
            justeras nypriserna, sedan följer annonspriserna på begagnat — det är dem prisindexet
            mäter — och till sist landar de verkliga affärerna, som hamnar i arkivet över sålda
            fordon. Historiskt ligger slutpriset hos handlare omkring tio procent under annonspriset,
            och det är den justeringen värderingsverktyget räknar med.
          </p>
          <p>
            I praktiken betyder det att du kan tidsätta ett köp eller en försäljning med några
            månaders framförhållning. Faller indexet två månader i rad kostar väntan pengar för den
            som ska sälja, för fordonet skrivs av samtidigt som nivån sjunker. Stiger indexet inför
            våren, vilket är det normala mönstret mars till juni, är det köparen som vinner på att
            agera tidigt.
          </p>
          <h2 className="font-display text-2xl text-foreground">Vad vi inte gör</h2>
          <p>
            Vi publicerar inte månader med färre än fem objekt, vi räknar inte om historiken när nytt
            underlag kommer in, och vi redovisar median i stället för snitt eftersom en integrerad
            husbil för 1,6 miljoner drar snittet flera hundra tusen men lämnar medianen orörd.
            Antalet objekt står alltid vid varje siffra, så du kan se hur tungt underlaget är innan du
            använder talet i en förhandling.
          </p>
          <h2 className="font-display text-2xl text-foreground">Vanliga frågor</h2>
          <dl className="space-y-4">
            {FAQ.map((f) => (
              <div key={f.q}>
                <dt className="font-medium text-foreground">{f.q}</dt>
                <dd className="mt-1">{f.a}</dd>
              </div>
            ))}
          </dl>
        </div>
      </section>

      <section className="container-edge pt-12">
        <RelatedLinks
          keyId="marknad"
          text="marknad prisindex prisstatistik börsen aktie värdering sålda fordon median prisutveckling"
          currentHref="/marknad"
        />
        <div className="pt-10">
          <AdSlot variant="multiplex" label="Relaterat innehåll" />
        </div>
      </section>
    </>
  );
}
